import {
  callApiPatch,
  callApiPost,
  constructApiUrl,
  createParams,
  validateParams,
  getChangedParams,
  getPolicyID,
  getRequestPreparePurchase,
} from './common';
import { createErrorBody } from '../error';
import { getPackagesWithCoverage } from '../package/packageUtils';
import { createPersonal, updatePersonal, preparePersonal } from '../../actions/Api';

const type = 'personal';

/**
 * Determines if the state allows us to run updates.
 * @param  {object} state The current state.
 * @return {boolean}      If we can update or not.
 */
const shouldUpdate = (state) => {
  return state.isUpdatingPersonal ? state.isUpdatingPersonal : true;
};

/**
 * Call the API using POST.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
const callPersonalApiPost = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPost(url, params, dispatch);
};

/**
 * Call the API using PATCH.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
const callPersonalApiPatch = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPatch(url, params, dispatch);
};

/**
 * Dispatches the create action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callCreatePersonal = (service, parameters) => {
  const params = parameters;
  let id = null;
  if (Object.hasOwnProperty.call(params, 'packageId')) {
    const { packageId } = params;
    id = packageId;
    delete params.packageId;
  }
  return dispatch => {
    return callPersonalApiPost(service, params, dispatch).then(data => {
      const newData = data;
      if (id) {
        newData.packageId = id;
      }
      return newData;
    });
  };
};

/**
 * Call the create  service.
 * @param  {Object} data The params to call the service with.
 * @return {Promise}     The promise of the service.
 */
export const requestCreatePersonal = (params, formValues) => {
  return (dispatch, getState) => {
    const state = getState();
    if (shouldUpdate(state)) {
      const mappedParams = params ? createParams(params, formValues, state) : {};
      // Ensure that we have the correct paramters. Fail if not.
      const errors = params ? validateParams(mappedParams, params) : [];
      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }
      const service = '';
      const serviceCalls = [];
      const packageInfo = getPackagesWithCoverage(state);
      dispatch(createPersonal());
      if (packageInfo.length > 0) {
        packageInfo.forEach(item => {
          serviceCalls.push(dispatch(callCreatePersonal(service, { ...mappedParams, ...item })));
        });
        return Promise.all(serviceCalls).then(data => {
          let response = {};
          if (data.length > 1) {
            data.forEach(element => {
              response[element.packageId] = element;
            });
          } else if (data.length === 1) {
            [response] = data;
          }
          return response;
        });
      }

      return dispatch(callCreatePersonal(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the update action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callUpdatePersonal = (service, params) => {
  return dispatch => {
    dispatch(updatePersonal());
    return callPersonalApiPatch(service, params, dispatch);
  };
};

/**
 * Call the update service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}           The promise of the service.
 */
export const requestUpdatePersonal = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      let mappedParams = params ? createParams(params, formValues, state) : {};
      const policyID = getPolicyID(state.userData, type);
      if (state.userData.personal) {
        // Find the changed values and pass those along.
        const additionalParams = { policyID };
        mappedParams = getChangedParams(mappedParams, state.userData.personal.usedParams, ['policyID'], additionalParams);
      }
      const service = `${policyID}`;
      return dispatch(callUpdatePersonal(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the prepare action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPreparePersonal = (service, params) => {
  return dispatch => {
    dispatch(preparePersonal());
    return callPersonalApiPatch(service, params, dispatch);
  };
};

/**
 * Call the prepare purchase service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}     The promise of the service.
 */
export const requestPreparePersonal = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const prepare = getRequestPreparePurchase(type, getState(), formValues, params);
      return dispatch(callPreparePersonal(prepare.service, prepare.mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};



// WEBPACK FOOTER //
// ./src/utilities/api/personal.js